import React, { useState, useEffect } from 'react';
import { Download, Mail, Phone, RotateCcw, AlertTriangle, Archive, FileText } from 'lucide-react';
import api from '../../api.js';

/**
 * Archive View - Shown to offboarded/archived customers
 *
 * When a tenant is offboarded:
 * - Shows friendly message explaining account is archived
 * - Provides data export download link (30-day availability)
 * - Shows contact info for reactivation
 * - Hides all regular app functionality
 */
function ArchiveView() {
  const [archiveInfo, setArchiveInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('fr'); // fr or en

  useEffect(() => {
    fetchArchiveInfo();
  }, []);

  const fetchArchiveInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenant/archive-info');
      setArchiveInfo(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch archive info:', err);
      setError(err.response?.data?.error || 'Impossible de charger les informations d\'archivage');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadData = () => {
    if (archiveInfo?.data_export?.download_url) {
      // For data URL (base64), trigger download
      const link = document.createElement('a');
      link.href = archiveInfo.data_export.download_url;
      link.download = `fleetmanager_export_${archiveInfo.tenant_code}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  const messages = archiveInfo?.messages?.[language] || archiveInfo?.messages?.fr;
  const dataExport = archiveInfo?.data_export;
  const contact = archiveInfo?.contact;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Archive className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">FleetManager</h1>
              <p className="text-sm text-gray-500">{archiveInfo?.tenant_name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setLanguage('fr')}
              className={`px-3 py-1 rounded ${language === 'fr' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              FR
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded ${language === 'en' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Status Banner */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 text-center border-t-4 border-amber-500">
          <Archive className="w-20 h-20 text-amber-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {messages?.title || 'Compte Archivé'}
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            {messages?.subtitle}
          </p>
          <p className="text-gray-500 max-w-2xl mx-auto">
            {messages?.description}
          </p>
          {archiveInfo?.effective_date && (
            <p className="text-sm text-gray-400 mt-4">
              {language === 'fr' ? 'Date de clôture' : 'Closed on'}: {formatDate(archiveInfo.effective_date)}
            </p>
          )}
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Data Export Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  {messages?.data_export_label || 'Télécharger mes données'}
                </h2>
                <p className="text-sm text-gray-600">
                  {messages?.data_export_description || 'Export complet de vos données'}
                </p>
              </div>
            </div>

            {dataExport?.available ? (
              <div>
                <button
                  onClick={handleDownloadData}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition shadow-md"
                >
                  <Download className="w-5 h-5" />
                  <span>{language === 'fr' ? 'Télécharger' : 'Download'}</span>
                </button>
                {dataExport.expires_at && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {language === 'fr' ? 'Disponible jusqu\'au' : 'Available until'}: {formatDate(dataExport.expires_at)}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  {language === 'fr' ? 'Export de données non disponible' : 'Data export not available'}
                </p>
                <p className="text-xs text-gray-500">
                  {language === 'fr' ? 'Contactez le support pour demander un export' : 'Contact support to request an export'}
                </p>
              </div>
            )}

            {archiveInfo?.deletion_scheduled && (
              <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-xs text-orange-700 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>
                    {language === 'fr' ? 'Suppression prévue le' : 'Deletion scheduled for'}: {formatDate(archiveInfo.deletion_scheduled)}
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Reactivation Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start space-x-3 mb-4">
              <RotateCcw className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  {messages?.reactivation_title || 'Réactiver mon compte'}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {messages?.reactivation_description}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {contact?.support_email && (
                <a
                  href={`mailto:${contact.support_email}`}
                  className="flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
                >
                  <Mail className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Email</p>
                    <p className="text-xs text-gray-600">{contact.support_email}</p>
                  </div>
                </a>
              )}
              {contact?.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
                >
                  <Phone className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{language === 'fr' ? 'Téléphone' : 'Phone'}</p>
                    <p className="text-xs text-gray-600">{contact.phone}</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
            <Mail className="w-6 h-6 text-blue-600 mr-2" />
            {messages?.support_title || 'Besoin d\'aide ?'}
          </h2>
          <p className="text-gray-600 mb-4">
            {messages?.support_description}
          </p>
          <div className="flex flex-wrap gap-4">
            {contact?.support_email && (
              <a
                href={`mailto:${contact.support_email}`}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition inline-flex items-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>Support</span>
              </a>
            )}
            {contact?.billing_email && (
              <a
                href={`mailto:${contact.billing_email}`}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition inline-flex items-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>{language === 'fr' ? 'Facturation' : 'Billing'}</span>
              </a>
            )}
          </div>
        </div>

        {/* Logout Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              localStorage.removeItem('token');
              window.location.href = '/login';
            }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            {language === 'fr' ? 'Se déconnecter' : 'Logout'}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8 text-center text-sm text-gray-500">
        <p>&copy; {new Date().getFullYear()} FleetManager. {language === 'fr' ? 'Tous droits réservés' : 'All rights reserved'}.</p>
      </footer>
    </div>
  );
}

export default ArchiveView;
